<script lang="ts">
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Dropdown from "$components/+blocks/Dropdown/Dropdown.svelte";
  import Action from "$components/+blocks/Dropdown/fields/Action.svelte";
  import Dialogue from "$components/+blocks/Dialogue/Dialogue.svelte";
  import { canAddDocument } from "$stores/permissions";
  import { saveToClipboard } from "app/utils/clipboard";
  import { docURL } from "app/utils/urls";
  import { alerts } from "app/stores/alert";
  import IconButtonTrigger from "$components/+blocks/Dropdown/fields/IconButtonTrigger.svelte";
  import { slugify } from "$utils/strings";
  import { Dots, Trash, Link, DashedCircle } from "$components/+blocks/OVIcon";
  import { onNavigate } from "$app/navigation";
  import { page } from "$app/stores";
  import { getStores } from "app/stores";
  import { getMediaQuery } from "app/utils/mediaQuery";
  import { createEventDispatcher, onMount, tick } from "svelte";
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import { isDarkTheme } from "app/stores/userPreferences";
  import { tooltip } from "app/actions/tooltip";
  import cx from "classnames";
  import type { WritableDocument } from "app/models/documents";

  export let document: WritableDocument;
  export let communitySlug: string;

  let isDeleteModalOpen = false;
  let isDropdownOpen = false;

  const { userPreferences } = getStores();
  $: path = docURL({
    communitySlug,
    docUuid: $document.uuid,
    docSlug: slugify($document.title),
  });
  $: isCurrentLocation = $page.url.pathname === path;

  onNavigate((nav) => {
    isCurrentLocation = nav.to?.url.pathname === path;
  });

  const dispatch = createEventDispatcher();
  const isDesktop = getMediaQuery("desktop");

  const expandGroup = async () => {
    await tick();
    if (isCurrentLocation) {
      dispatch("expand-group");
    }
  };

  $: isCurrentLocation && expandGroup();

  onMount(async () => {
    await expandGroup();
  });
</script>

<!-- TODO: replace [#707070] with new gray when the new gray system is implemented -->
<a
  data-sveltekit-preload-data
  on:click={() => !$isDesktop && userPreferences.toggleLeftPanelExpansion()}
  href={path}
  aria-current={isCurrentLocation ? "page" : false}
  class={cx(
    isCurrentLocation
      ? "font-semibold text-community-primary"
      : "font-normal text-[#707070] hover:text-neutral-700/90 dark:text-neutral-400 dark:hover:text-neutral-300",
    "group flex h-[32px] items-center gap-2.5 overflow-hidden rounded pr-1 pl-2 text-sm transition-colors duration-100 ease-out md:text-[13px]"
  )}
>
  <!-- truncate less when showing the dropdown menu button -->
  <span
    class="flex min-w-0 grow items-center truncate group-hover:pr-0"
    class:!pr-0={isDropdownOpen}
    class:pr-4={!isDropdownOpen}
    title={$document.title}
    use:tooltip
  >
    {#if $document.icon}
      {@const icon = $isDarkTheme && $document.iconDark ? $document.iconDark : $document.icon}
      <div class="mr-2">
        <Avatar mainImage={icon} radius="rounded-[3px]" />
      </div>
    {/if}
    <p class="truncate">{$document.title}</p>
  </span>

  <div class="group-hover:block" class:block={isDropdownOpen} class:hidden={!isDropdownOpen}>
    <Dropdown bind:open={isDropdownOpen}>
      <IconButtonTrigger
        size="small"
        slot="trigger"
        icon={Dots}
        a11yLabel="More actions"
        let:active
        {active}
      />

      <Action
        title="Copy link"
        icon={Link}
        on:click={() =>
          saveToClipboard(
            docURL(
              {
                docUuid: $document.uuid,
                docSlug: slugify($document.title),
                communitySlug,
              },
              true
            ),
            alerts.add
          )}
      />
      {#if $canAddDocument}
        <Action title="Delete" icon={Trash} danger on:click={() => (isDeleteModalOpen = true)} />
      {/if}
    </Dropdown>
  </div>
  {#if $document.isDraft}
    <div class="group-hover:hidden" class:hidden={isDropdownOpen}>
      <OVIcon icon={DashedCircle} class="mr-0.5 shrink-0 text-orange-500 dark:text-orange-400" />
    </div>
  {/if}
</a>

<Dialogue
  bind:open={isDeleteModalOpen}
  title="Delete {$document.title}"
  message="Are you sure you want to delete {$document.title}?"
  confirmLabel="Delete"
  on:confirm={() => {
    document.delete();
  }}
  danger
/>
